import {ApiOptions, ApiParams, ApiService} from './api.service';
import {CreateEntity, Entity, entityId, EntityId, EntityModel, PagedList, UpdateEntity} from '../models/entity.model';
import {map, Observable} from 'rxjs';
import {ModelMapper} from './model-mapper';

type Constructor<T> = new (...args: any[]) => T;

export interface ListApiOptions {
  page?: number
  pageSize?: number
  params?: ApiParams
}

export abstract class EntityApiService<T extends Entity, TModel extends EntityModel<T>, TCreate = CreateEntity<T>> extends ApiService {
  protected readonly mapper: ModelMapper<T, TModel>

  protected constructor(
    model: Constructor<TModel>,
    protected readonly controller: string,
    protected readonly parentController?: string,
  ) {
    super();
     this.mapper = new ModelMapper(model)
  }

  list(scope: EntityId, options?: ListApiOptions): Observable<PagedList<TModel>>
  list(options?: ListApiOptions): Observable<PagedList<TModel>>
  list(scopeOrOptions: EntityId | ListApiOptions = {}, options?: ListApiOptions): Observable<PagedList<TModel>> {
    let scope: EntityId | undefined

    if (typeof scopeOrOptions === 'string' || 'id' in scopeOrOptions) {
      scope = scopeOrOptions as EntityId
      options = options || {}
    } else {
      options = scopeOrOptions as ListApiOptions
    }

    const params = options.params || {}
    if (options.page) params['page[page]'] = options.page.toString()
    if (options.pageSize) params['page[limit]'] = options.pageSize.toString()

    return this.mapper.mapList(super.get<PagedList<T>>(
      this.apiUrlFor(scope, { parent: true, params }),
    ))
  }

  // @ts-ignore
  get(id: EntityId, params?: Record<string, string>): Observable<TModel> {
    return this.mapper.map(super.get<T>(
      this.apiUrlFor(id, { params})
    ))
  }

  create(request: TCreate): Observable<TModel>
  create(scope: EntityId, request: TCreate): Observable<TModel>
  create(p1: EntityId | TCreate, p2?: TCreate): Observable<TModel> {
    if (this.parentController) {
      if (!p2) throw new Error('Expected scope and request parameters')
      return this.mapper.map(super.post<T>(this.apiUrlFor(p1 as EntityId, { parent: true }), p2))
    } else {
      if (p2) throw new Error('Expected only request parameters')
      return this.mapper.map(super.post<T>(this.apiUrlFor(), p1))
    }
  }

  update(request: UpdateEntity<T>): Observable<TModel> {
    return this.mapper.map(super.put<T>(this.apiUrlFor(request), request))
  }

  // @ts-ignore
  delete(id: EntityId): Observable<boolean> {
    return super.delete<any>(this.apiUrlFor(id)).pipe(map(() => true))
  }
  protected apiUrlFor(
    target?: EntityId,
    {parent = false, params}: { parent?: boolean, params?: Record<string, string>} = {}
  ): string {
    const id = entityId(target)
    const query = params ? '?' + new URLSearchParams(params).toString() : ''

    if (parent && this.parentController) {
      if (!id) throw new Error('Cannot resolve parent URL without target')
      return `${this.parentController}/${id}/${this.controller}${query}`
    } else if (!id) {
      return `${this.controller}${query}`;
    } else {
      return `${this.controller}/${id}${query}`
    }
  }
}
